import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que la pantalla de clientes le pide a la API (#104), con la forma del
// `datos.ts` de sucursales: clave de caché con el taller adentro, una consulta
// por lectura y una función por escritura que devuelve el `ProblemDetails` o nada.
//
// Lo propio de clientes: la edición guarda la ficha entera con sus contactos en
// un solo PUT, y dos 409 traen datos —el cliente que ya tiene el documento, y
// los folios que impiden eliminarlo—.

export type Cliente = components['schemas']['CustomerListItem'];
export type FichaCliente = components['schemas']['CustomerDetail'];
export type TipoDocumento = components['schemas']['DocumentTypeListItem'];
export type ClienteExistente = components['schemas']['ExistingCustomer'];
export type ActivoCliente = components['schemas']['AssetListItem'];
export type OrdenCliente = components['schemas']['CustomerOrderListItem'];
export type OrdenesCliente = components['schemas']['CustomerOrdersResponse'];
export type VistaImportacion = components['schemas']['CustomerImportPreview'];
export type ResultadoImportacion = components['schemas']['ImportCustomersResponse'];
export type FilaConError = components['schemas']['CustomerImportRowError'];
/** El formato del archivo: viaja en la URL (`?format=`), no como esquema. */
export type FormatoExportacion = 'xlsx' | 'csv' | 'pdf';
/** La plantilla no se ofrece en PDF: no se puede llenar. */
export type FormatoPlantilla = Exclude<FormatoExportacion, 'pdf'>;
type Clase = components['schemas']['CustomerKind'];

/**
 * Una fila de la tabla de contactos. `id` es el del contacto que ya existía, o
 * `null` si es nuevo: **sin él, la API quita el viejo y crea otro**, así que se
 * conserva mientras el tipo no cambie (el backend no le cambia el tipo a un
 * contacto; cambiarlo es quitar y agregar).
 */
export interface ContactoFila {
  id: string | null;
  kind: 'phone' | 'email';
  value: string;
  label: string;
  isPrimary: boolean;
}

/// Lo que se manda al crear o editar. Los nombres son los del contrato
/// (`docTypeId`, `docIdentifier`): los errores por campo de la API llegan con
/// esas claves, y un campo con otro nombre no los dibujaría (#76).
export interface ValoresCliente {
  kind: Clase;
  code: string;
  name: string;
  /** Solo empresas (#126): al pasar a Persona se vacía, o la API lo rechaza. */
  tradeName: string;
  docTypeId: string;
  docIdentifier: string;
  address: string;
  notes: string;
  /** Solo el alta: nacen como el teléfono y el correo principales. */
  phone: string;
  email: string;
  /** Solo la edición: la lista completa de teléfonos y correos. */
  contacts: ContactoFila[];
  /** Solo la edición: el cliente nace activo. */
  isActive: boolean;
}

export interface FiltrosClientes {
  /** Nombre, código, documento, teléfono o correo; los dos últimos, completos. */
  buscar: string;
  tipo: 'todos' | Clase;
  estado: 'todos' | 'activos' | 'inactivos';
  cursor: string | null;
}

/** Los 409 de clientes con datos. `ProblemaApi` solo trae el código y los campos. */
export type ProblemaCliente = ProblemaApi & {
  existingCustomer?: ClienteExistente | null;
  /** Los tres, solo con `customer.tiene_historial` (#177). */
  orderCount?: number | string | null;
  assetCount?: number | string | null;
  isActive?: boolean | null;
};

/// El taller de la pestaña (#205): va en toda clave de caché (regla 5 de multitenancy,
/// en el navegador). Ver `sucursales/datos.ts`.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['clientes', taller] as const,
  lista: (taller: string, filtros: FiltrosClientes) =>
    ['clientes', taller, 'lista', filtros] as const,
  ficha: (taller: string, id: string) => ['clientes', taller, 'ficha', id] as const,
  // Bajo `activos` y no bajo `clientes`: lo que invalida un cambio en un activo
  // —editarlo, eliminarlo, agregarlo desde la ficha— es esa rama.
  activos: (taller: string, id: string) => ['activos', taller, 'cliente', id] as const,
  ordenes: (taller: string, id: string) => ['clientes', taller, 'ordenes', id] as const,
  // Bajo `clientes`: guardar, activar o desactivar invalidan esa rama, y el
  // historial se relee solo.
  historial: (taller: string, id: string) => ['clientes', taller, 'historial', id] as const,
  // Fuera de `clientes`: guardar invalida esa rama, y volver a pedir el código
  // justo antes de salir del alta sería una consulta para nadie.
  siguienteCodigo: (taller: string) => ['siguienteCodigoCliente', taller] as const,
  tiposDocumento: (taller: string, aplicaA: Clase) => ['tiposDocumento', taller, aplicaA] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/** Espacios afuera, y vacío pasa a `null`: la API guarda «sin teléfono», no un texto vacío. */
const opcional = (texto: string) => texto.trim() || null;

export function useClientes(filtros: FiltrosClientes) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.lista(taller, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/customers', {
        params: {
          // Solo lo que tiene valor: un vacío es un filtro, no su ausencia.
          query: {
            ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
            ...(filtros.tipo !== 'todos' && { kind: filtros.tipo }),
            ...(filtros.estado !== 'todos' && { isActive: filtros.estado === 'activos' }),
            ...(filtros.cursor && { cursor: filtros.cursor }),
          },
        },
      });
      if (error || !data) throw new Error('No se pudo leer la lista de clientes');
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useFichaCliente(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/customers/{id}', {
        params: { path: { id: id ?? '' } },
      });
      // El 404 no es un fallo de red: el cliente no existe, está eliminado o es
      // de otro taller. La pantalla lo dice distinto.
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer el cliente');
      return data;
    },
    // Siempre fresca: con la versión vieja en caché, guardar chocaría (#77).
    staleTime: 0,
  });
}

/**
 * El historial del cliente (#181): quién lo creó y quién le cambió qué, lo más
 * nuevo primero. Pide lo mismo que la ficha, `customer.edit`. Los datos personales
 * llegan sin valor desde el servidor: la auditoría nunca los guardó.
 *
 * Solo se pide con el panel abierto: no al abrir la ficha (#193).
 */
export function useHistorialCliente(id: string, abierto: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.historial(taller, id),
    enabled: abierto,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/customers/{id}/history', { params: { path: { id } } });
      if (error || !data) throw new Error('No se pudo leer el historial del cliente');
      return data;
    },
  });
}

/**
 * Los activos del cliente, para su ficha (#105). Es `GET /api/assets` con el
 * filtro de cliente —la misma consulta de la pantalla Activos, para no tener
 * dos— y el tope de una página: un cliente de un taller mediano no llega a cien
 * carros, y si llegara se pagina entonces.
 */
export function useActivosCliente(id: string) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.activos(taller, id),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/assets', {
        params: { query: { customerId: id, limit: 100 } },
      });
      if (error || !data) throw new Error('No se pudieron leer los activos del cliente');
      return data.items;
    },
  });
}

/**
 * Las últimas cinco órdenes del cliente. Solo se pide con `order.view`: sin él
 * la API responde 403, y la sección no se dibuja.
 *
 * El total puede venir nulo aunque la orden tenga monto: **lo calla la API** a
 * quien no tiene `order.amount.view`. La pantalla no decide eso, solo dibuja «—».
 */
export function useOrdenesCliente(id: string, habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ordenes(taller, id),
    enabled: habilitado,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/customers/{id}/orders', {
        params: { path: { id } },
      });
      if (error || !data) throw new Error('No se pudieron leer las órdenes del cliente');
      return data;
    },
  });
}

/**
 * Los tipos de documento que aplican a la clase de cliente. Al cambiar Persona ↔
 * Empresa se sigue viendo la lista anterior mientras llega la nueva: un tipo que
 * aplica a las dos clases no parpadea a vacío.
 */
export function useTiposDocumento(aplicaA: Clase) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.tiposDocumento(taller, aplicaA),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/document-types', {
        params: { query: { appliesTo: aplicaA } },
      });
      if (error || !data) throw new Error('No se pudieron leer los tipos de documento');
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * El código que tendría el próximo cliente, para mostrarlo ya puesto en el alta.
 * No reserva nada: el alta lo manda vacío si nadie lo tocó.
 *
 * Sin caché (`gcTime: 0`): cada alta pregunta de nuevo. Uno guardado de la visita
 * anterior ya lo habría usado otro cliente.
 */
export function useSiguienteCodigo(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.siguienteCodigo(taller),
    enabled: habilitado,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/customers/next-code');
      if (error || !data) throw new Error('No se pudo leer el siguiente código');
      return data.code;
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Otro código para «Guardar y agregar otro», o vacío si no se pudo saber. Es una
 * petición suelta y no un `refetch()` de la consulta de arriba: si la primera
 * falló, volver a pedirla la pone en `pending` y la pantalla desmontaría el
 * formulario en pleno alta.
 */
export async function pedirSiguienteCodigo(): Promise<string> {
  try {
    const { data } = await api.GET('/api/customers/next-code');
    return data?.code ?? '';
  } catch {
    return '';
  }
}

/**
 * Las escrituras. Cada una devuelve el `ProblemDetails` si la API dijo que no, o
 * nada si salió bien —la forma que espera `useFormulario`—, y al salir bien
 * invalida todo lo de clientes del taller.
 */
export function useCambiosCliente() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras<T extends { error?: unknown }>(
    peticion: Promise<T>,
  ): Promise<ProblemaCliente | undefined> {
    const { error } = await peticion;
    if (error) return error as ProblemaCliente;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return undefined;
  }

  return {
    crear: (v: ValoresCliente) =>
      tras(
        api.POST('/api/customers', {
          body: {
            kind: v.kind,
            name: v.name.trim(),
            tradeName: opcional(v.tradeName),
            // Vacío deja que lo asigne el correlativo al guardar.
            code: opcional(v.code),
            ...(v.docTypeId && { docTypeId: v.docTypeId }),
            docIdentifier: opcional(v.docIdentifier),
            phone: opcional(v.phone),
            email: opcional(v.email),
            address: opcional(v.address),
            notes: opcional(v.notes),
          },
        }),
      ),

    editar: (id: string, versionActual: number, v: ValoresCliente) =>
      tras(
        api.PUT('/api/customers/{id}', {
          params: { path: { id } },
          body: {
            version: versionActual,
            kind: v.kind,
            name: v.name.trim(),
            tradeName: opcional(v.tradeName),
            code: v.code.trim(),
            isActive: v.isActive,
            ...(v.docTypeId && { docTypeId: v.docTypeId }),
            docIdentifier: opcional(v.docIdentifier),
            address: opcional(v.address),
            notes: opcional(v.notes),
            contacts: v.contacts.map((c) => ({
              ...(c.id && { id: c.id }),
              kind: c.kind,
              value: c.value.trim(),
              label: opcional(c.label),
              isPrimary: c.isPrimary,
            })),
          },
        }),
      ),

    cambiarEstado: (id: string, versionActual: number, activo: boolean) =>
      tras(
        api.PUT('/api/customers/{id}/status', {
          params: { path: { id } },
          body: { version: versionActual, isActive: activo },
        }),
      ),

    eliminar: async (id: string): Promise<ProblemaCliente | undefined> => {
      const { error } = await api.DELETE('/api/customers/{id}', { params: { path: { id } } });
      if (error) return error as ProblemaCliente;
      // Todo lo de clientes menos lo que la ficha abierta pide del eliminado
      // (#190): volver a pedirlo daría tres 404, y la ficha diría «no existe»
      // justo mientras se sale. Como en activos y en el catálogo.
      const eliminadas = [claves.ficha(taller, id), claves.historial(taller, id), claves.ordenes(taller, id)];
      await cache.invalidateQueries({
        queryKey: claves.todo(taller),
        predicate: (consulta) =>
          !eliminadas.some((clave) => clave.every((parte, i) => consulta.queryKey[i] === parte)),
      });
      return undefined;
    },

    /**
     * Vuelve a leer clientes después de un rechazo. `tras` solo recarga si salió
     * bien; una ventana que reintenta tras un conflicto necesita la versión nueva,
     * o cada intento choca otra vez (#177).
     */
    refrescar: () => cache.invalidateQueries({ queryKey: claves.todo(taller) }),
  };
}

/**
 * Lo que sale de un paso de la importación: lo que respondió la API, o el
 * `ProblemDetails` si dijo que no.
 */
export type Paso<T> = { datos: T; problema?: undefined } | { datos?: undefined; problema: ProblemaCliente };

/**
 * La importación desde un archivo (#106). Los dos pasos mandan **el mismo
 * archivo**: la API no guarda nada entre la revisión y la importación, y vuelve a
 * revisar antes de dar de alta.
 *
 * El contrato tipa el archivo como `string` porque así describe OpenAPI a un
 * `IFormFile`; lo que viaja es el `File`, armado a mano en el `FormData`, igual
 * que en los adjuntos.
 */
export function useImportacionClientes() {
  const taller = useTaller();
  const cache = useQueryClient();

  const conArchivo = (archivo: File) => ({
    body: { file: archivo as unknown as string },
    bodySerializer: () => {
      const formulario = new FormData();
      formulario.append('file', archivo, archivo.name);
      return formulario;
    },
  });

  return {
    revisar: async (archivo: File): Promise<Paso<VistaImportacion>> => {
      const { data, error } = await api.POST('/api/customers/import/preview', conArchivo(archivo));
      return data ? { datos: data } : { problema: (error || {}) as ProblemaCliente };
    },

    importar: async (archivo: File): Promise<Paso<ResultadoImportacion>> => {
      const { data, error } = await api.POST('/api/customers/import', conArchivo(archivo));
      // Aunque la importación falle, se recarga igual: si se cortó a mitad, los
      // lotes anteriores ya quedaron guardados y el listado cambió.
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      // `error` llega como "" si la respuesta no trae cuerpo (un 413, un 502): por
      // eso se pregunta por `data` y el problema cae a `{}`.
      return data ? { datos: data } : { problema: (error || {}) as ProblemaCliente };
    },

    /** Baja la plantilla y la ofrece para guardar. `false` si la API no la dio. */
    plantilla: async (formato: FormatoPlantilla): Promise<boolean> => {
      const { data, response } = await api.GET('/api/customers/import/template', {
        params: { query: { format: formato } },
        parseAs: 'blob',
      });
      if (!data) return false;
      guardarArchivo(data, response, `plantilla-clientes.${formato}`);
      return true;
    },
  };
}

/**
 * Baja los clientes que muestra el listado (#107), como la exportación de
 * Productos/Servicios: mismos filtros que `useClientes`, y con el estado en
 * «Todos» también los inactivos, porque el listado los muestra.
 */
export async function exportar(
  formato: FormatoExportacion,
  filtros: Omit<FiltrosClientes, 'cursor'>,
): Promise<ProblemaApi | undefined> {
  const { data, error, response } = await api.GET('/api/customers/export', {
    params: {
      query: {
        format: formato,
        ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
        ...(filtros.tipo !== 'todos' && { kind: filtros.tipo }),
        ...(filtros.estado === 'todos'
          ? { includeInactive: true }
          : { isActive: filtros.estado === 'activos' }),
      },
    },
    parseAs: 'blob',
  });
  // Con el cuerpo vacío, openapi-fetch deja `error` en `""`, que es falso: sin
  // el objeto, la pantalla lo tomaría como éxito sin haber bajado nada.
  if (error !== undefined || !data) return (typeof error === 'object' && error ? error : {}) as ProblemaApi;
  guardarArchivo(data, response, `clientes.${formato}`);
  return undefined;
}

/** Entrega el archivo al navegador con el nombre que puso la API (`clientes-2026-10-06.xlsx`), o uno genérico. */
function guardarArchivo(datos: Blob, response: Response, alterno: string) {
  const disposicion = response.headers.get('Content-Disposition') ?? '';
  const nombre =
    /filename\*=UTF-8''([^;]+)/i.exec(disposicion)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposicion)?.[1] ?? alterno;

  const enlace = document.createElement('a');
  enlace.href = URL.createObjectURL(datos);
  enlace.download = decodeURIComponent(nombre);
  enlace.click();
  URL.revokeObjectURL(enlace.href);
}
